import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocalSearchParams } from "expo-router";
// @ts-expect-error baseline 2026-02-16
import { WebLayout } from "@src/components/web";
import { View } from "@src/components";
import { DataStateView } from "@src/components/screen-state";
import { useCameras } from "@src/features/cameras";
import { useTenant } from "@src/features/tenants";
import { useTrafficFlowScenarios } from "../hooks";
import { useScenarioCoordinatesStore, EMPTY_SCENARIO_CONFIG } from "../stores/scenario-coordinates-store";
import { groupScenariosBySite, type ScenarioSiteGroup } from "../utils/scenario-group";
import { ScenarioGrid } from "../components/ScenarioGrid.web";
import { ScenarioDetailPanel } from "../components/ScenarioDetailPanel.web";
import { ScenarioMap } from "../components/ScenarioMap.web";
import { MapCanvas } from "../components/MapCanvas.web";
import { ViewModeToggle, type TrafficFlowViewMode } from "../components/ViewModeToggle.web";

const VIEW_MODE_KEY = "traffic-flow:view-mode";

function loadViewMode(): TrafficFlowViewMode {
  if (typeof window === "undefined") return "grid";
  const v = window.localStorage.getItem(VIEW_MODE_KEY);
  return v === "map" ? "map" : "grid";
}

interface TrafficFlowScreenProps {
  /**
   * Site segment from the URL path. Source of truth for filtering.
   * Absence (undefined) means the bare /traffic-flow path = explicit all-sites,
   * not "fall back to the store's last selection".
   */
  siteIdFromRoute?: string;
}

export default function TrafficFlowScreen({ siteIdFromRoute }: TrafficFlowScreenProps = {}) {
  const {
    data: cameras,
    isLoading: isCamerasLoading,
    isFetching: isCamerasFetching,
    error: camerasError,
    refetch: refetchCameras,
  } = useCameras();
  const {
    data: scenarios,
    isLoading: isScenariosLoading,
    isFetching: isScenariosFetching,
    error: scenariosError,
    refetch: refetchScenarios,
    isTenantPending,
  } = useTrafficFlowScenarios();
  // URL is the source of truth for site filter — `siteIdFromRoute` comes from
  // the [siteId] route segment, or undefined on the bare /traffic-flow URL
  // (= explicit all-sites). No store fall-through: the store can drift.
  const selectedSiteId = siteIdFromRoute;
  const { selectedTenant } = useTenant();
  const tenantId = selectedTenant?.id ?? "";
  const configByUuid = useScenarioCoordinatesStore((s) => s.byTenant[tenantId] ?? EMPTY_SCENARIO_CONFIG);

  const allGroups = useMemo(
    () => (cameras && scenarios ? groupScenariosBySite(scenarios, cameras, configByUuid) : undefined),
    [cameras, scenarios, configByUuid],
  );

  const filteredGroups = useMemo(() => {
    if (!allGroups) return undefined;
    if (!selectedSiteId) return allGroups;
    return allGroups.filter((g) => g.siteId === selectedSiteId);
  }, [allGroups, selectedSiteId]);

  const selectedSiteName = useMemo(() => {
    if (!selectedSiteId || !allGroups) return null;
    return allGroups.find((g) => g.siteId === selectedSiteId)?.siteName ?? null;
  }, [allGroups, selectedSiteId]);

  const { selectedScenario } = useLocalSearchParams<{ selectedScenario?: string }>();
  // Look up the selected scenario across ALL sites, not just the filtered set —
  // hard-refreshing on a `?selectedScenario=...` URL whose scenario lives in a
  // different site than the dropdown filter must still render the panel.
  const detailRow = useMemo(() => {
    if (!selectedScenario || !allGroups) return null;
    for (const group of allGroups) {
      const row = group.rows.find((r) => r.scenarioUuid === selectedScenario);
      if (row) return row;
    }
    return null;
  }, [allGroups, selectedScenario]);

  const rightPanel =
    detailRow ?
      <ScenarioDetailPanel
        // Remount when the selection changes so per-scenario state (period
        // dropdown, future scroll position, etc.) resets to defaults.
        key={detailRow.scenarioUuid}
        scenarioUuid={detailRow.scenarioUuid}
        scenarioName={detailRow.scenarioName}
        cameraDisplayName={detailRow.cameraDisplayName}
      />
    : undefined;

  const [viewMode, setViewMode] = useState<TrafficFlowViewMode>(loadViewMode);
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  const flatRows = useMemo(() => (filteredGroups ?? []).flatMap((g) => g.rows), [filteredGroups]);
  // Stable identity so MapCanvas's marker-sync effect doesn't re-fire on
  // unrelated parent re-renders (which would re-attach every pin's click
  // listener and refresh marker styling for all scenarios). Pin clicks share
  // the same URL state as grid card clicks — `?selectedScenario=<uuid>`;
  // wire-up of the actual navigation is pending the URL plumbing in MR-D.
  const handleScenarioSelect = useCallback((_scenarioUuid: string) => {
    // no-op until URL plumbing lands
  }, []);

  return (
    <WebLayout rightPanel={rightPanel}>
      <View flex={1} style={{ paddingTop: 16, paddingHorizontal: 24, gap: 16 }}>
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
        <DataStateView<ScenarioSiteGroup[]>
          data={filteredGroups}
          isLoading={isTenantPending || isCamerasLoading || isScenariosLoading}
          isFetching={isCamerasFetching || isScenariosFetching}
          error={camerasError ?? scenariosError ?? null}
          refetch={() => {
            void refetchCameras();
            void refetchScenarios();
          }}
          isEmpty={(g) => g.reduce((sum, group) => sum + group.rows.length, 0) === 0}
          loadingMessage="Loading scenarios..."
          errorTitle="Unable to load scenarios"
          errorMessage="Please check your connection and try again."
          emptyState={
            selectedSiteId ?
              {
                icon: "motion",
                title: `No scenarios at ${selectedSiteName ?? "this site"}`,
                message: "Pick a different site, or 'All sites' to see traffic-flow scenarios from elsewhere.",
              }
            : {
                icon: "motion",
                title: "No traffic-flow scenarios reported yet",
                message: "Cameras configured for traffic-flow will appear here once they publish scenario status.",
              }
          }
        >
          {(groups) =>
            viewMode === "map" ?
              <ScenarioMap
                rows={flatRows}
                selectedScenarioUuid={selectedScenario ?? null}
                onScenarioSelect={handleScenarioSelect}
              />
            : <View flex={1} style={{ gap: 16, minHeight: 0 }}>
                <MapCanvas
                  rows={flatRows}
                  selectedScenarioUuid={selectedScenario ?? null}
                  onScenarioSelect={handleScenarioSelect}
                  height={260}
                />
                <View flex={1} style={{ minHeight: 0 }}>
                  <ScenarioGrid groups={groups} selectedSiteName={selectedSiteName} />
                </View>
              </View>
          }
        </DataStateView>
      </View>
    </WebLayout>
  );
}
